import { cn, Dialog, focusRing } from "@simpro/ui";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { SectionHeading } from "@/shared/components/SectionHeading";
import { Reveal } from "@/shared/motion/Reveal";
import { page } from "@/shared/styles/layout";
import { pill } from "@/shared/styles/pill";
import type { PublicImage } from "../schemas/home.schemas";

/**
 * The board's strip of shapes, in turn: a soft landscape, a tall arch, a wide soft rectangle, a circle,
 * a large soft rectangle and a narrow arch. Each keeps its proportions and narrows on a phone.
 */
const SHAPES = [
  "w-[26.25rem] aspect-[420/320] rounded-[2rem]",
  "w-[16.25rem] aspect-[260/380] arch",
  "w-[23.75rem] aspect-[380/280] rounded-[2rem]",
  "w-[18.75rem] aspect-square rounded-full",
  "w-[27.5rem] aspect-[440/360] rounded-[2rem]",
  "w-[17.5rem] aspect-[280/400] arch",
] as const;

export interface GallerySectionProps {
  heading: string | null;
  text: string | null;
  images: readonly PublicImage[];
  /** The Gallery page has something to show, so the section links to it (FR-SA-195). */
  viewAll?: boolean;
}

/**
 * The salon's own photographs (design note salon-home-landing.md section 5 item 5; TDD-SA FR-SA-191).
 * Each photo opens full size, with arrows between them and Esc to close; the dialog keeps focus inside
 * and gives it back to the photo it came from.
 *
 * Drawn as the Organic Wellness board's strip (design note salon-booking-organic-wellness.md section 6
 * item 9): the pictures in a row the width of the screen, in its six shapes in turn (soft rectangles,
 * arches and a circle), centred on one line, scrolling sideways and snapping. Each zooms slowly under
 * the pointer and rises into view in turn.
 *
 * With a Gallery page to go to (FR-SA-195) the heading has "View all"; a salon that filled its gallery
 * but chose no pictures for home still gets the heading and the link. Nothing at all when there is
 * neither.
 */
export function GallerySection({ heading, text, images, viewAll = false }: GallerySectionProps) {
  const [open, setOpen] = useState<number | null>(null);
  if (images.length === 0 && !viewAll) return null;
  const shown = open === null ? null : images[open];
  const step = (by: number) => setOpen((at) => (at === null ? at : (at + by + images.length) % images.length));

  return (
    <section aria-labelledby="gallery-heading" className="flex flex-col gap-10 lg:gap-12">
      <SectionHeading
        id="gallery-heading"
        className={page}
        title={heading ?? "Our work"}
        lead={text}
        action={
          viewAll ? (
            <Link to="/gallery" viewTransition className={cn(pill.base, pill.md, pill.outline, focusRing)}>
              View all{" "}<span className="sr-only">pictures and videos</span>
            </Link>
          ) : null
        }
      />

      {images.length > 0 ? (
        <ul className="flex snap-x snap-mandatory scroll-px-4 items-center gap-4 overflow-x-auto px-4 pb-3 md:scroll-px-8 md:px-8 lg:scroll-px-16 lg:px-16">
          {images.map((image, index) => (
            <Reveal
              as="li"
              key={image.url}
              index={index}
              className={cn("max-w-[80vw] shrink-0 snap-start overflow-hidden", SHAPES[index % SHAPES.length])}
            >
              <button
                type="button"
                onClick={() => setOpen(index)}
                aria-label={`Open photo ${index + 1} of ${images.length}${image.altText ? `: ${image.altText}` : ""}`}
                className={cn("group block size-full overflow-hidden bg-muted-bg", focusRing)}
              >
                <img
                  src={image.url}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="size-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.2,0.7,0.2,1)] group-hover:scale-[1.04] motion-reduce:transition-none"
                />
              </button>
            </Reveal>
          ))}
        </ul>
      ) : null}

      <Dialog
        open={shown !== null}
        onOpenChange={(next) => !next && setOpen(null)}
        title={`Photo ${(open ?? 0) + 1} of ${images.length}`}
        size="xl"
      >
        {shown ? (
          <div className="flex flex-col gap-3">
            <img src={shown.url} alt={shown.altText} className="max-h-[70vh] w-full rounded-lg object-contain" />
            {images.length > 1 ? (
              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => step(-1)}
                  aria-label="Previous photo"
                  className={cn("grid size-11 place-items-center rounded-full border border-edge text-heading", focusRing)}
                >
                  <ChevronLeft className="size-5" aria-hidden />
                </button>
                <p className="text-sm text-subtle">{shown.altText}</p>
                <button
                  type="button"
                  onClick={() => step(1)}
                  aria-label="Next photo"
                  className={cn("grid size-11 place-items-center rounded-full border border-edge text-heading", focusRing)}
                >
                  <ChevronRight className="size-5" aria-hidden />
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </Dialog>
    </section>
  );
}
